import { useState, useRef, useEffect, useCallback } from 'react';
import type { MouseEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectMpoeLink, selectUserData } from '@store/userData/selectors';
import { selectSessionList } from '@store/sessions/selectors';
import { SessionsItem } from '@store/sessions/types';

import { initiateLogout } from '@store/userData/actions';
import { checkSavedSessionHandler } from '@utils';

const useProfileMenu = (openModal: () => void) => {
	const dispatch = useDispatch();
	const menuRef = useRef<HTMLDivElement>(null);
	const [isMenuOpen, setMenuOpenState] = useState(false);
	const userData = useSelector(selectUserData);
	const mpoeLink = useSelector(selectMpoeLink);
	const sessionsList = useSelector(selectSessionList);

	const logOutHandler = useCallback(() => dispatch(initiateLogout()), [dispatch]);

	const openStateHandler = () => setMenuOpenState(true);
	const closeStateHandler = () => setMenuOpenState(false);
	useEffect(() => {
		if (!menuRef.current) {
			return;
		}
		const element = menuRef.current;
		element.addEventListener('casMenuOpened', openStateHandler);
		element.addEventListener('casMenuClosed', closeStateHandler);
		return () => {
			if (!element) {
				return;
			}
			element.removeEventListener('casMenuOpened', openStateHandler);
			element.removeEventListener('casMenuClosed', closeStateHandler);
		};
	}, [setMenuOpenState]);

	const mpoeRedirectionHandler = useCallback(() => window.open(mpoeLink, '_blank'), [mpoeLink]);

	const checkIfSavedHandler = useCallback(
		(e: MouseEvent) => {
			e.stopPropagation();

			if (sessionsList.length === 0) {
				logOutHandler();
				return;
			}

			const sessionsSavedStates = sessionsList.map((sessionItem: SessionsItem) =>
				checkSavedSessionHandler(sessionItem.sessionId),
			);
			const isSaved = !sessionsSavedStates.some(elem => !elem);
			isSaved ? logOutHandler() : openModal();
		},
		[sessionsList, logOutHandler, openModal],
	);

	return {
		isMenuOpen,
		userData,
		menuRef,
		mpoeLink,
		mpoeRedirectionHandler,
		logOutHandler,
		checkIfSavedHandler,
	};
};
export default useProfileMenu;
